/**
 * Signatory Service
 *
 * Handles fetching law firm users who can act as signatories
 */

import { apiGet, isSuccessResponse } from '@/lib/utils/api-helpers';
import { apiClient } from '@/lib/api/server-client';

/**
 * Raw law-firm user record as returned by the backend account users endpoint.
 * Kept permissive (Record<string, unknown>) at the boundary so downstream
 * transformers can pick fields without locking the shape in this layer.
 */
export type LawFirmUserRecord = Record<string, unknown>;

interface PaginatedUsersResponse {
  items?: LawFirmUserRecord[];
  meta?: { totalItems?: number };
}

function extractItems(
  data: PaginatedUsersResponse | LawFirmUserRecord[] | unknown
): LawFirmUserRecord[] {
  if (Array.isArray(data)) {
    return data as LawFirmUserRecord[];
  }
  if (
    data &&
    typeof data === 'object' &&
    Array.isArray((data as PaginatedUsersResponse).items)
  ) {
    return (data as PaginatedUsersResponse).items as LawFirmUserRecord[];
  }
  console.error('Unexpected user data structure:', data);
  return [];
}

/**
 * Fetch law firm users who can act as signatories
 * Fetches from account users endpoint with pagination
 *
 * CLIENT-SIDE VERSION (for backward compatibility)
 */
export async function getSignatories(): Promise<LawFirmUserRecord[]> {
  try {
    // Use apiGet to fetch law firm users directly from the API
    const usersResponse = await apiGet<
      PaginatedUsersResponse | LawFirmUserRecord[]
    >(`/api/proxy/account/users?limit=100`);

    console.log('Law firm users API response:', usersResponse);

    if (isSuccessResponse(usersResponse)) {
      return extractItems(usersResponse.data);
    }

    return [];
  } catch (error) {
    console.error('Failed to fetch law firm users for signatories:', error);
    throw error; // Let the error propagate - the user needs to be authenticated
  }
}

/**
 * Fetch law firm users who can act as signatories
 * SERVER-SIDE VERSION (for use in server actions)
 */
export async function getSignatoriesServer(): Promise<LawFirmUserRecord[]> {
  try {
    // Use server-side API client
    const usersData = await apiClient.get<
      PaginatedUsersResponse | LawFirmUserRecord[]
    >('/account/users?limit=100');

    return extractItems(usersData);
  } catch (error) {
    console.error('Failed to fetch law firm users for signatories:', error);
    throw error;
  }
}
